import type {
  BlindWindow,
  Check,
  ComponentConfig,
  ComponentState,
  ConfigHistoryEntry,
  Health,
  Incident,
  Proposal,
  PublishAttempt,
  QuarantineEntry,
  Run,
  Scope,
} from '@/domain/types';
import { SCOPES, componentKey } from '@/domain/types';

import { CATALOG, isJobCheck, type CatalogEntry } from './catalog';
import { Random } from './random';

/**
 * The mock's whole state: one object the MockConsoleApi reads and mutates. Keys mirror the
 * DynamoDB tables (TDD §9). Runs are kept newest first, as the evaluator reads them.
 */
export interface World {
  now: string;
  rng: Random;
  configs: Map<string, ComponentConfig>;
  states: Map<string, ComponentState>;
  runs: Map<string, Run[]>;
  proposals: Map<string, Proposal>;
  attempts: Map<string, PublishAttempt[]>;
  blindWindows: Map<string, BlindWindow[]>;
  quarantine: QuarantineEntry[];
  history: Map<string, ConfigHistoryEntry[]>;
  incidents: Map<Scope, Incident[]>;
  pageUrls: Record<Scope, string>;
  /** Proposal ids whose first approve is raced by the evaluator (TDD §5.6): a PASS run lands first. */
  raceOnce: Set<string>;
  /** Components whose next publish fails three times; cleared after it does, so Retry now succeeds. */
  failNextPublish: Set<string>;
}

const DEFAULT_LADDER = { '3': 'impaired', '6': 'degraded', '12': 'failed' } as const;

/** How each scenario shapes a component's newest runs. */
type Pattern =
  | { kind: 'healthy' }
  | {
      kind: 'failing';
      failStreak: number;
      driving: string;
      errorCode: string;
      errorMessage: string;
    }
  | {
      kind: 'recovering';
      passStreak: number;
      priorFails: number;
      driving: string;
      errorCode: string;
      errorMessage: string;
    }
  | { kind: 'silent'; lastRunMinutesAgo: number }
  | { kind: 'never' };

interface Scenario {
  pattern: Pattern;
  published?: Health;
  autoRecovery?: boolean;
  publishUncertain?: boolean;
  /** A closed blind window this long ago (hours), lasting this many cycles; draws a gap in run history. */
  pastGap?: { hoursAgo: number; cycles: number };
}

/** Failures with the check that drives them, one per scenario. */
const ERRORS = {
  csvImportJob: {
    driving: 'csv_import_job_completes',
    errorCode: 'job_timeout',
    errorMessage: 'import job still RUNNING after 600 s',
  },
  csvExport: {
    driving: 'csv_export_completes',
    errorCode: 'http_500',
    errorMessage: 'export request returned 500 Internal Server Error',
  },
  cedeExport: {
    driving: 'cede_export_completes',
    errorCode: 'export_failed',
    errorMessage: 'export ended in FAILED state: exposure view locked',
  },
  lossJob: {
    driving: 'loss_analysis_job_completes',
    errorCode: 'job_timeout',
    errorMessage: 'loss analysis not complete after 900 s',
  },
  upload: {
    driving: 'presigned_upload',
    errorCode: 'http_503',
    errorMessage: 'upload to the pre-signed URL returned 503 Service Unavailable',
  },
  sql: {
    driving: 'execute_sql_query',
    errorCode: 'query_timeout',
    errorMessage: 'Redshift query did not return within 60 s',
  },
  login: {
    driving: 'okta_login',
    errorCode: 'http_401',
    errorMessage: 'Okta federation refused the login: 401 invalid_client',
  },
  programJob: {
    driving: 'program_analysis_job_completes',
    errorCode: 'job_failed',
    errorMessage: 'program analysis ended in FAILED state',
  },
} as const;

/** The story each scope tells. Everything not listed is healthy and published ok. */
const SCENARIOS: Partial<Record<Scope, Partial<Record<string, Scenario>>>> = {
  'us-prod': {
    // Open failure proposal: ok → Partial Outage after 7 failing runs.
    'import.csv': { pattern: { kind: 'failing', failStreak: 7, ...ERRORS.csvImportJob } },
    // Recovery awaiting a person (auto_recovery off), waiting longer than the alert threshold.
    'export.csv': {
      pattern: { kind: 'recovering', passStreak: 4, priorFails: 5, ...ERRORS.csvExport },
      published: 'impaired',
      autoRecovery: false,
    },
    // Approved, then the publish failed three times: sits in the queue with Retry now.
    'export.cede': { pattern: { kind: 'failing', failStreak: 12, ...ERRORS.cedeExport } },
    // Ongoing incident: published Partial Outage, evidence agrees, nothing to propose.
    'loss.analysis': {
      pattern: { kind: 'failing', failStreak: 9, ...ERRORS.lossJob },
      published: 'degraded',
    },
    // Blind: the runner stopped 70 minutes ago.
    activity: { pattern: { kind: 'silent', lastRunMinutesAgo: 70 } },
    // Under maintenance: nothing is proposed while a person holds the status.
    'tenant.mgmt': { pattern: { kind: 'healthy' }, published: 'maintenance' },
    // Open proposal whose first approve is raced by a PASS run (see raceOnce).
    'synergy.drive': { pattern: { kind: 'failing', failStreak: 4, ...ERRORS.upload } },
    // Last write got no response.
    'data.query': { pattern: { kind: 'healthy' }, publishUncertain: true },
    'import.cede': { pattern: { kind: 'healthy' }, pastGap: { hoursAgo: 26, cycles: 8 } },
  },
  'us-uat': {
    // Rejected proposal held in the slot: a known test-data reset, not a fault.
    'data.query': { pattern: { kind: 'failing', failStreak: 4, ...ERRORS.sql } },
  },
  'eu-prod': {
    // De-escalation: published Major Outage, the streak has since shortened to 7 runs.
    'login.sso': {
      pattern: { kind: 'failing', failStreak: 7, ...ERRORS.login },
      published: 'failed',
    },
    // Open proposal whose publish will fail three times when approved.
    program: {
      pattern: { kind: 'failing', failStreak: 3, ...ERRORS.programJob },
    },
  },
  'eu-uat': {
    // Configured but never reported: the reachability check design is still open.
    vpn: { pattern: { kind: 'never' } },
  },
};

const PEOPLE = ['Priya Natarajan', 'Tom Whitfield', 'Ana Ferreira'];

export function buildWorld(nowMs: number = Date.now()): World {
  const rng = new Random(20260917);
  const now = new Date(nowMs).toISOString();
  const world: World = {
    now,
    rng,
    configs: new Map(),
    states: new Map(),
    runs: new Map(),
    proposals: new Map(),
    attempts: new Map(),
    blindWindows: new Map(),
    quarantine: [],
    history: new Map(),
    incidents: new Map(),
    pageUrls: {
      'us-prod': 'https://status.verisk.com/us-prod',
      'us-uat': 'https://status.verisk.com/us-uat',
      'eu-prod': 'https://status.verisk.com/eu-prod',
      'eu-uat': 'https://status.verisk.com/eu-uat',
    },
    raceOnce: new Set(),
    failNextPublish: new Set(),
  };

  for (const scope of SCOPES) {
    world.incidents.set(scope, []);
    for (const entry of CATALOG) {
      const scenario = SCENARIOS[scope]?.[entry.id] ?? { pattern: { kind: 'healthy' } };
      seedComponent(world, scope, entry, scenario, nowMs);
    }
  }

  seedQuarantine(world, nowMs);
  return world;
}

function seedComponent(
  world: World,
  scope: Scope,
  entry: CatalogEntry,
  scenario: Scenario,
  nowMs: number,
): void {
  const key = componentKey(scope, entry.id);
  const appliedAt = new Date(
    nowMs - (scenario.pattern.kind === 'never' ? 3 * 3600_000 : 30 * 24 * 3600_000),
  ).toISOString();
  const config: ComponentConfig = {
    scope,
    capability_id: entry.id,
    display_name: entry.display_name,
    source: entry.source,
    checks: [...entry.checks],
    checks_applied_at: appliedAt,
    ladder: { ...DEFAULT_LADDER },
    recovery_threshold_runs: 2,
    auto_recovery: scenario.autoRecovery ?? true,
    recovery_approval_alert_min: 30,
    runner_cadence_minutes: 15,
    staleness_tolerance_cycles: 2,
    applied_at: appliedAt,
    version: 1,
    updated_by: 'Ana Ferreira',
    updated_at: appliedAt,
  };
  world.configs.set(key, config);
  world.history.set(key, [
    {
      component_key: key,
      version: '0001',
      changed_at: appliedAt,
      changed_by: 'Ana Ferreira',
      reason: 'Initial configuration for the Synergy Studio status monitor.',
      before: null,
      after: { ...config },
    },
  ]);
  // A later edit for the asynchronous-job components, so history has more than one row.
  if (entry.id === 'import.csv' || entry.id === 'loss.analysis') {
    const at = new Date(nowMs - 9 * 24 * 3600_000).toISOString();
    const before = {
      ...config,
      ladder: { '2': 'impaired', '4': 'degraded', '8': 'failed' } as const,
    };
    config.version = 2;
    config.updated_at = at;
    config.updated_by = 'Tom Whitfield';
    config.applied_at = at;
    world.history.get(key)!.push({
      component_key: key,
      version: '0002',
      changed_at: at,
      changed_by: 'Tom Whitfield',
      reason:
        'Ladder was too twitchy for a job that legitimately takes several minutes; back to 3/6/12.',
      before: { ...before, version: 1 },
      after: { ...config },
    });
  }

  const runs = generateRuns(world.rng, key, entry, config, scenario, nowMs);
  world.runs.set(key, runs);
  world.blindWindows.set(key, []);

  const newest = runs[0] ?? null;
  const state: ComponentState = {
    scope,
    capability_id: entry.id,
    evaluated_through: newest?.run_key ?? null,
    evaluated_at: newest?.received_at ?? null,
    observed_outcome: null,
    driving_check: null,
    fail_streak: 0,
    pass_streak: 0,
    last_observed_at: newest?.completed_at ?? null,
    implied_status: 'ok',
    blind_since: null,
    open_proposal_id: null,
    published_status: scenario.published ?? 'ok',
    publish_uncertain: scenario.publishUncertain ?? false,
  };
  world.states.set(key, state);

  // Blindness (TDD §7.4): the detector noticed within 5 minutes of the threshold.
  const toleranceMs = config.staleness_tolerance_cycles * config.runner_cadence_minutes * 60_000;
  const lastObservedMs = newest ? Date.parse(newest.completed_at) : Date.parse(config.applied_at);
  if (nowMs - lastObservedMs >= toleranceMs) {
    const openedAt = new Date(lastObservedMs + toleranceMs + 2 * 60_000).toISOString();
    state.blind_since = openedAt;
    world.blindWindows.get(key)!.push({
      component_key: key,
      opened_at: openedAt,
      last_observed_at: newest?.completed_at ?? null,
      last_run_key: newest?.run_key ?? null,
      closed_at: null,
      missed_runs_at_close: null,
    });
  }
  if (scenario.pastGap) {
    const start = nowMs - scenario.pastGap.hoursAgo * 3600_000;
    const cadenceMs = config.runner_cadence_minutes * 60_000;
    const lastBefore = runs.find((r) => Date.parse(r.completed_at) < start);
    const openedAt = new Date(start + toleranceMs + 60_000).toISOString();
    const closedAt = new Date(
      start + scenario.pastGap.cycles * cadenceMs + 3 * 60_000,
    ).toISOString();
    world.blindWindows.get(key)!.push({
      component_key: key,
      opened_at: openedAt,
      last_observed_at: lastBefore?.completed_at ?? null,
      last_run_key: lastBefore?.run_key ?? null,
      closed_at: closedAt,
      missed_runs_at_close: scenario.pastGap.cycles,
    });
  }
}

/**
 * Generate two days of runs at the component's cadence, newest first. The scenario decides
 * the newest runs; further back, an occasional isolated failure keeps the history honest.
 */
function generateRuns(
  rng: Random,
  key: string,
  entry: CatalogEntry,
  config: ComponentConfig,
  scenario: Scenario,
  nowMs: number,
): Run[] {
  const { pattern } = scenario;
  if (pattern.kind === 'never') return [];
  const cadenceMs = config.runner_cadence_minutes * 60_000;
  const horizon = 48 * 3600_000;
  const lastRunAgoMs =
    pattern.kind === 'silent' ? pattern.lastRunMinutesAgo * 60_000 : rng.int(2, 9) * 60_000;
  const gapStart = scenario.pastGap ? nowMs - scenario.pastGap.hoursAgo * 3600_000 : null;
  const gapEnd =
    scenario.pastGap && gapStart ? gapStart + scenario.pastGap.cycles * cadenceMs : null;

  const runs: Run[] = [];
  let index = 0;
  for (
    let completedMs = nowMs - lastRunAgoMs;
    completedMs > nowMs - horizon;
    completedMs -= cadenceMs
  ) {
    if (gapStart !== null && gapEnd !== null && completedMs >= gapStart && completedMs < gapEnd) {
      continue;
    }
    const durationMs = entry.async ? rng.int(150, 380) * 1000 : rng.int(12, 45) * 1000;
    const startedMs = completedMs - durationMs;
    const failing = isFailing(pattern, index, rng, config.checks[0] ?? 'check');
    const checks = makeChecks(rng, config.checks, startedMs, completedMs, failing);
    const startedAt = new Date(startedMs).toISOString();
    const runId = `run-${rng.hex(4)}`;
    runs.push({
      component_key: key,
      run_key: `${startedAt}#${runId}`,
      source: config.source,
      run_id: runId,
      started_at: startedAt,
      completed_at: new Date(completedMs).toISOString(),
      received_at: new Date(completedMs + rng.int(800, 2600)).toISOString(),
      checks,
    });
    index += 1;
  }
  return runs;
}

interface Failing {
  driving: string;
  errorCode: string;
  errorMessage: string;
}

function isFailing(
  pattern: Pattern,
  indexFromNewest: number,
  rng: Random,
  firstCheck: string,
): Failing | null {
  switch (pattern.kind) {
    case 'failing':
      if (indexFromNewest < pattern.failStreak) return pattern;
      break;
    case 'recovering':
      if (
        indexFromNewest >= pattern.passStreak &&
        indexFromNewest < pattern.passStreak + pattern.priorFails
      ) {
        return pattern;
      }
      break;
    default:
      break;
  }
  // Deep history: a rare isolated failure, never within the newest 15 runs so streaks are untouched.
  if (indexFromNewest > 15 && rng.chance(0.02)) {
    return { driving: firstCheck, errorCode: 'http_502', errorMessage: 'gateway returned 502' };
  }
  return null;
}

function makeChecks(
  rng: Random,
  names: string[],
  startedMs: number,
  completedMs: number,
  failing: Failing | null,
): Check[] {
  const span = completedMs - startedMs;
  const checks: Check[] = [];
  let failed = false;
  names.forEach((name, i) => {
    const observedMs = startedMs + Math.round((span * (i + 1)) / (names.length + 0.5));
    const observed_at = new Date(Math.min(observedMs, completedMs)).toISOString();
    if (failed) {
      checks.push({ name, outcome: 'NOT_RUN', observed_at });
      return;
    }
    if (failing && name === failing.driving) {
      failed = true;
      checks.push({
        name,
        outcome: 'FAIL',
        observed_at,
        duration_ms: isJobCheck(name) ? rng.int(600_000, 900_000) : rng.int(1200, 9000),
        error_code: failing.errorCode,
        error_message: failing.errorMessage,
        artifact_url: `https://github.com/verisk/synergy-checks/actions/runs/${rng.int(41_000_000, 41_999_999)}`,
      });
      return;
    }
    checks.push({
      name,
      outcome: 'PASS',
      observed_at,
      duration_ms: isJobCheck(name) ? rng.int(120_000, 420_000) : rng.int(180, 4200),
    });
  });
  return checks;
}

function seedQuarantine(world: World, nowMs: number): void {
  const at = (minutesAgo: number) => new Date(nowMs - minutesAgo * 60_000).toISOString();
  const payload = (scope: Scope, cap: string, extra: string) =>
    `{"source":"gha-synergy-imports","run_id":"run-9A1F","scope":"${scope}","capability_id":"${cap}",${extra}}`;
  world.quarantine.push(
    {
      quarantine_id: '01K5GAA3F8Q6ZJ3TQ6Y2BHK7N1',
      source: 'gha-synergy-imports',
      received_at: at(131),
      claimed_scope: 'us-prod',
      claimed_capability_id: 'import.cede',
      http_status: 422,
      reason: 'check_set_mismatch',
      detail:
        'run carried check cede_import_job_submitted; the configured set is [cede_import_job_completes] (status reflects completion, not submission)',
      raw_payload: payload(
        'us-prod',
        'import.cede',
        '"checks":[{"name":"cede_import_job_submitted","outcome":"PASS"}]',
      ),
      raw_payload_truncated: false,
    },
    {
      quarantine_id: '01K5GAB9X0M2D7W1RZ5C4VJ8P3',
      source: 'gha-synergy-imports',
      received_at: at(94),
      claimed_scope: 'us-prod',
      claimed_capability_id: 'import.cede',
      http_status: 409,
      reason: 'id_reused',
      detail: 'run_id run-9A1F was first received 2 hours earlier with a different body',
      raw_payload: payload('us-prod', 'import.cede', '"started_at":"…"'),
      raw_payload_truncated: true,
    },
    {
      quarantine_id: '01K5GAC1T4N8H2K9QW7E3YD6R5',
      source: 'gha-legacy-imports',
      received_at: at(47),
      claimed_scope: 'eu-uat',
      claimed_capability_id: 'import.csv',
      http_status: 422,
      reason: 'source_mismatch',
      detail: 'configured source is gha-synergy-imports',
      raw_payload: payload('eu-uat', 'import.csv', '"checks":[]').replace(
        'gha-synergy-imports',
        'gha-legacy-imports',
      ),
      raw_payload_truncated: false,
    },
  );
}

export const MOCK_PEOPLE = PEOPLE;
